import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { safeSentryCapture } from '@/utils/sentry';

interface HealthCheckOptions {
  interval?: number; // milliseconds, default 60000 (60s)
  failureThreshold?: number; // consecutive failures before alert, default 3
  enabled?: boolean; // default true
}

export function useHealthCheck(options: HealthCheckOptions = {}) {
  const {
    interval = 60000,
    failureThreshold = 3,
    enabled = true,
  } = options;

  const { toast } = useToast();
  const [isHealthy, setIsHealthy] = useState(true);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const checkHealth = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/health`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        const data = await response.json();
        const healthy = data.status === 'healthy';

        if (healthy) {
          // Reset failure counter on success
          if (consecutiveFailures > 0) {
            setConsecutiveFailures(0);
            toast({
              title: 'Connection Restored',
              description: 'Services are back online.',
              variant: 'default',
            });
          }
          setIsHealthy(true);
        } else {
          // Increment failure counter
          const newFailureCount = consecutiveFailures + 1;
          setConsecutiveFailures(newFailureCount);

          if (newFailureCount >= failureThreshold) {
            setIsHealthy(false);
            toast({
              title: 'Service Degraded',
              description: 'Some services are experiencing issues.',
              variant: 'destructive',
            });
            
            safeSentryCapture('captureException', new Error('Health check failure threshold exceeded'), {
              extra: {
                consecutiveFailures: newFailureCount,
                healthStatus: data,
              },
            });
          }
        }

        setLastCheck(new Date());

      } catch (error) {
        const newFailureCount = consecutiveFailures + 1;
        setConsecutiveFailures(newFailureCount);

        if (newFailureCount >= failureThreshold) {
          setIsHealthy(false);
          toast({
            title: 'Connection Issue',
            description: 'Unable to reach services. Please check your connection.',
            variant: 'destructive',
          });

          safeSentryCapture('captureException', error as Error, {
            extra: {
              context: 'health_check',
              consecutiveFailures: newFailureCount,
            },
          });
        }

        setLastCheck(new Date());
      }
    };

    // Initial check
    checkHealth();

    // Set up interval
    const intervalId = setInterval(checkHealth, interval);

    return () => clearInterval(intervalId);
  }, [enabled, interval, failureThreshold, consecutiveFailures, toast]);

  return {
    isHealthy,
    consecutiveFailures,
    lastCheck,
  };
}
